<script setup lang="ts" generic="TValue extends string | number = string | number">
/**
 * UI-kit component
 * Category: Selects
 * Name: 'AppSelect'
 * FIGMA: https://www.figma.com/design/oRsJUOAucnhxx5aeqy4TRO/APP-admin?node-id=16127-221763
 */
import { computed } from 'vue'

import type { AppSelectProps } from '../types/interfaces'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { faXmark } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'
import { AppDropdown, AppDropdownItem } from '@shared/ui/dropdowns'

import { useAppSelectClasses } from '../composables/use-app-select-classes.hook'
import { useAppSelect } from '../composables/use-app-select.hook'

defineOptions({
  name: 'AppSelect',
})

const props = withDefaults(defineProps<AppSelectProps<TValue>>(), {
  placeholder: '',
  label: undefined,
  optionalText: undefined,
  required: false,
  state: 'static',
  disabled: false,
  message: undefined,
  prependIcon: undefined,
  panelMinWidth: undefined,
})

const emit = defineEmits<{
  open: []
  close: []
}>()

// `update:modelValue` is auto-emitted by `defineModel`.
const modelValue = defineModel<TValue>()

defineSlots<{
  'prepend-icon'?: () => unknown
  option?: (scope: {
    option: AppSelectOption<TValue>
    selected: boolean
    disabled: boolean
  }) => unknown
  selected?: (scope: { option: AppSelectOption<TValue> }) => unknown
}>()

const {
  triggerRef,
  inputRef,
  panelRef,
  isOpen,
  panelId,
  labelId,
  floatingStyles,
  isDisabled,
  isError,
  isSuccess,
  isHighlighted,
  displayedOptions,
  resolvedSelectedOption,
  inputValue,
  inputPlaceholder,
  showClearValueButton,
  focusInput,
  handleChevronClick,
  handleClearValueClick,
  handleInputFocus,
  handleTriggerKeydown,
  handleSelect,
} = useAppSelect<TValue>({
  modelValue,
  props,
  onOpen: () => emit('open'),
  onClose: () => emit('close'),
})

// Default-skin visuals (classes / message color / chevron icon) live in the
// view layer, not the headless core.
const { rootClasses, triggerClasses, messageColor, chevronIcon } = useAppSelectClasses({
  isOpen,
  isDisabled,
  isError,
  isSuccess,
})

// Narrow `resolvedSelectedOption` for the #selected slot scope. The slot is
// only rendered when the value is defined (see template v-if), so the
// non-null assertion is sound — and this gives consumers a non-nullable
// `option` in their slot template.
const selectedForSlot = computed(() => resolvedSelectedOption.value!)
</script>

<template>
  <div :class="rootClasses">
    <div
      v-if="label"
      class="app-select__label-row"
    >
      <AppText
        :id="labelId"
        name="body2"
        color="slate-900"
        class="app-select__label"
      >
        {{ label }}<span
          v-if="required"
          class="app-select__required"
        >*</span>
      </AppText>
      <AppText
        v-if="optionalText"
        name="body2"
        color="slate-400"
        class="app-select__optional"
      >
        {{ optionalText }}
      </AppText>
    </div>

    <div
      ref="triggerRef"
      :class="triggerClasses"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-controls="panelId"
      @click="focusInput"
    >
      <span
        v-if="$slots['prepend-icon'] || prependIcon"
        class="app-select__prepend"
      >
        <slot name="prepend-icon">
          <AppIcon
            v-if="prependIcon"
            :icon="prependIcon"
          />
        </slot>
      </span>

      <span
        v-if="$slots.selected && resolvedSelectedOption"
        class="app-select__selected"
      >
        <slot
          name="selected"
          :option="selectedForSlot"
        />
      </span>

      <input
        ref="inputRef"
        :value="inputValue"
        type="text"
        :class="[
          'app-select__input',
          { 'app-select__input--hidden': $slots.selected && resolvedSelectedOption },
        ]"
        :placeholder="inputPlaceholder"
        :disabled="isDisabled"
        readonly
        :aria-labelledby="label ? labelId : undefined"
        :aria-required="required || undefined"
        :aria-invalid="isError || undefined"
        @focus="handleInputFocus"
        @keydown="handleTriggerKeydown"
      >

      <button
        v-if="showClearValueButton"
        type="button"
        class="app-select__clear-btn"
        :disabled="isDisabled"
        tabindex="-1"
        aria-label="Clear selection"
        @mousedown.prevent
        @click.stop="handleClearValueClick"
      >
        <AppIcon :icon="faXmark" />
      </button>
      <button
        v-else
        type="button"
        class="app-select__chevron-btn"
        :disabled="isDisabled"
        tabindex="-1"
        aria-label="Toggle dropdown"
        @mousedown.prevent
        @click.stop="handleChevronClick"
      >
        <AppIcon :icon="chevronIcon" />
      </button>
    </div>

    <AppText
      v-if="message"
      name="body2"
      :color="messageColor"
      class="app-select__message"
    >
      {{ message }}
    </AppText>

    <Teleport to="body">
      <div
        v-if="isOpen"
        :id="panelId"
        ref="panelRef"
        role="listbox"
        class="app-select__panel"
        :style="floatingStyles"
      >
        <AppDropdown>
          <div
            v-for="(option, index) in displayedOptions"
            :key="String(option.value)"
            :data-app-select-option="index"
            :class="[
              'app-select__option',
              {
                'app-select__option--highlighted': isHighlighted(index),
                'app-select__option--selected': option.value === modelValue,
                'app-select__option--disabled': option.disabled === true,
              },
            ]"
            role="option"
            :aria-selected="option.value === modelValue"
            :aria-disabled="option.disabled || undefined"
            tabindex="-1"
            @click="handleSelect(option as AppSelectOption<TValue>)"
          >
            <slot
              name="option"
              :option="option"
              :selected="option.value === modelValue"
              :disabled="option.disabled === true"
            >
              <AppDropdownItem
                tag="div"
                :text="option.text"
                :selected="option.value === modelValue"
                :disabled="option.disabled"
                tabindex="-1"
              />
            </slot>
          </div>
        </AppDropdown>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss" src="../styles/_app-select.scss" />
